import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth';
import apiInstance from '../utils/axios';
import {
  getContextualResponse,
  getStructuredIntent,
  correctTypos,
  generateProductResponse,
  generateOrderResponse
} from '../utils/freeLLM';
import './CleanChatbot.css';

const CleanChatbot = () => {
  // =======================
  // STATE & REFS
  // =======================
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { user } = useAuthStore();

  // =======================
  // HELPERS
  // =======================
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getDynamicSuggestions = () => [
    '¿Tienes iPhone 16 disponible?',
    '¿Cuál fue mi último pedido?',
    'Muéstrame mochilas',
    'Precios de laptops',
    'Ayuda con entrega'
  ];

  const getAIGreeting = () => {
    const userName =
      user?.full_name ||
      user?.username ||
      user?.email?.split('@')[0] ||
      'amigo';

    const isLoggedIn = user && (user.user_id || user.id);

    return isLoggedIn
      ? `¡Hola ${userName}! 👋 Soy tu asistente de compras. ¿En qué puedo ayudarte hoy?`
      : `¡Hola! 👋 Soy tu asistente de compras. Inicia sesión para ayuda personalizada.`;
  };

  // =======================
  // EFFECTS
  // =======================
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: Date.now(),
          text: getAIGreeting(),
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
      setSuggestions(getDynamicSuggestions());
    }
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // =======================
  // CHAT LOGIC
  // =======================
  const getSmartResponse = async (message) => {
    try {
      const corrected = correctTypos(message);
      const intentData = await getStructuredIntent(corrected, messages);
      const { intent, entities } = intentData;

      if (intent === 'small_talk' || intent === 'help') {
        return await getContextualResponse(corrected, messages);
      }

      if (intent === 'search_product') {
        const query = entities?.products?.join(' ') || corrected;
        const res = await apiInstance.get(`/search/?query=${encodeURIComponent(query)}`);
        const products = Array.isArray(res.data) ? res.data : [];

        if (!products.length) {
          return `No encontré productos para "${query}".`;
        }

        return await generateProductResponse(products, query, 'es');
      }

      return await getContextualResponse(corrected, messages);
    } catch (err) {
      console.error(err);
      return 'Ocurrió un error, pero sigo aquí para ayudarte 🙂';
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMsg = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);

    const response = await getSmartResponse(inputMessage);

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: response,
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
      setIsTyping(false);
    }, 1200);
  };

  // =======================
  // RENDER
  // =======================
  return (
    <div>
      {!isOpen && (
        <div className="clean-chatbot-toggle">
          <button
            onClick={() => setIsOpen(true)}
            className="clean-chatbot-btn"
            aria-label="Open AI Assistant"
            title="Herramientas Tayrun AI"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15C21 16.1 20.1 17 19 17H7L3 21V5C3 3.9 3.9 3 5 3H19C20.1 3 21 3.9 21 5V15Z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>
      )}

      {isOpen && <div>TEMP</div>}
    </div>
  );
};

export default CleanChatbot;
